<script setup>
import ShareNetwork from '/app/components/ShareNetwork.vue';
import TwitterPreview from './TwitterPreview.vue';
import { SOLUTION_FORMAT } from '/app/constant/challenge';
import { stripHtml, currency } from '@bignerve/ui-utils';
import { get } from 'lodash';
import { isAMA, isCaption } from '/app/utils/challenge';
import { useFeatureFlag } from '/app/composables/feature-flag';
import { useAuth } from '/app/composables/auth';
import { useSharing } from '/app/composables/sharing';
import { useBus } from '/app/composables/bus';
import { sentry } from '/app/plugins/sentry';
import { api } from '/app/plugins/services';
import { ref, reactive, computed, watch, nextTick, onMounted, useTemplateRef } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import NIcon from '/app/components/NIcon.vue';
import NLoading from '/app/components/NLoading.vue';
import NAlert from '/app/components/NAlert.vue';

const isChallenge = (target) => target.hasOwnProperty('solution_format');
const handleAccessor = (user, fallback) => user?.handle || user?.screen_name || fallback;

const props = defineProps({
    item: {
        type: Object,
        default: () => ({}),
    },
    query: {
        type: Object,
        default: () => ({}),
    },
    defaultContent: {
        type: String,
        default: '',
    },
});

const emit = defineEmits(['done']);

const { auth } = useAuth();
const { expect } = useFeatureFlag('survey-mode-link');
const surveyModeEnabled = expect(() => auth.user.id);
const { snoozeds, fetchSnoozed, setSnoozeds } = useSharing();
const { emit: busEmit } = useBus();
const route = useRoute();
const router = useRouter();
const input = useTemplateRef('input');

const snoozing = ref(false);
const preparing = ref(false);
const copied = ref(false);
const surveyMode = ref(false);
const error = ref(null);
const content = ref(props.defaultContent);
const networks = ref([
    { network: 'twitter', name: 'Twitter', icon: 'twitter', enabled: true, sharing: false },
    { network: 'facebook', name: 'Facebook', icon: 'facebook-f-brands', enabled: true, sharing: false },
    { network: 'email', name: 'email', icon: 'envelope-regular', enabled: true, sharing: false },
    { network: 'imgur', name: 'Imgur', icon: 'imgur', enabled: false, sharing: false },
    { network: 'reddit', name: 'Reddit', icon: 'reddit-alien-brands', enabled: true, sharing: false },
]);
const sharing = reactive({
    url: '',
    subject: '',
    title: 'BigNerve',
    description: '',
    quote: '',
    hashtags: '',
    credentials: [],
});

const target = computed(() => props.item);
const challenge = computed(() => isChallenge(target.value) ? target.value : {});
const user = computed(() => auth.current.user);

const queryString = computed(() => {
    const q = {
        scout_id: null,
        ...( !isChallenge(target.value) ? route.query : {}),
        ...props.query,
    };

    if (user.value) {
        q.scout_id = handleAccessor(user.value);
    }

    return q;
});

const resolvedRoute = computed(() => {
    let r = {};
    if (isChallenge(target.value)) {
        r = resolveChallengeRoute();
    } else if (handleAccessor(target.value.user)) {
        r = resolveUserRoute();
    }

    const resolved = router.resolve(Object.assign({}, r, { query: queryString.value }));

    return resolved ? resolved.href : '/';
});

const url = computed(() => window.location.origin + resolvedRoute.value);

const canCopy = computed(() => Boolean(typeof navigator !== 'undefined' && 'clipboard' in navigator));

const totalSnoozed = computed(() => snoozeds.value.length);

const snoozed = computed(() =>
    snoozeds.value.some((s) =>
        s.payload.metadata.target === target.value.slug,
    ),
);

const isOwner = computed(() =>
    auth.authenticated
        && challenge.value.catalyst
        && challenge.value.catalyst.id === auth.user.id,
);

const surveyModeAllowed = computed(() =>
    challenge.value.solution_format === SOLUTION_FORMAT.CHALLENGE
        && !challenge.value.is_closed
        && !isCaption(challenge.value)
        && !challenge.value.idea_entry_fee
        && surveyModeEnabled.value,
);

async function copy() {
    input.value.select();
    input.value.setSelectionRange(0, 99999);

    await window.navigator.clipboard.writeText(url.value);

    copied.value = true;
    setTimeout(() => (copied.value = false), 5000);
}

async function prepareSharing() {
    preparing.value = true;

    if (isChallenge(target.value)) {
        const data = await resolveChallengeData(target.value);

        if (!data) {
            preparing.value = false;
            return;
        }

        sharing.title = data.text;
        sharing.description = data.description;
        sharing.hashtags = data.hashtags.join(',');
        sharing.credentials = data.metadata.credentials || [];
        sharing.subject = 'challenge';
        sharing.url = url.value;

        content.value = data;
    }
    if (handleAccessor(target.value.user)) {
        await resolveUserData();

        sharing.title = [
            `Join in the ${target.value.user.first_name} ${target.value.user.last_name} #ideatournament`,
            target.value.deep_prize && `- overall prize $${target.value.deep_prize}.`,
            target.value.twitter && `@${target.value.twitter}`,
        ].filter((v) => v).join(' ');
        sharing.description = stripHtml(target.value.user.slogan);
        sharing.hashtags = ['bignerve'].join(',');
        sharing.url = url.value;
        sharing.subject = 'user';
    }

    preparing.value = false;
}

async function resolveChallengeData({ slug }) {
    try {
        const { data: response } = await api.sharing.intent({
            options: { ...props.query, skip_ideas: false, survey_mode: surveyMode.value },
            subject: 'challenge',
            target: slug,
        });

        return response.data;
    } catch (err) {
        console.log(err);

        sentry.withScope((scope) => {
            scope.setTag('event', 'sharing-user');
            scope.setExtra('target', target.value);
            scope.setExtra('sharing', sharing);

            sentry.captureException(err);
        });
    }
}

async function resolveUserData() {
    try {
        const targetUserHandle = handleAccessor(target.value.user);
        const currentUserHandle = handleAccessor(auth.user);
        const useTournament = (slug) => api.widgets.olympiads(
            'base_olympiad_id', { platform_slug: slug },
        );

        if (!auth.authenticated || currentUserHandle !== targetUserHandle) {
            const social = await api.user.social(targetUserHandle).then((r) => r.data.data);
            const twitter = get(social, 'accounts.twitter.handle', null);

            target.value.twitter = twitter;
        }

        const userTournament = await useTournament(targetUserHandle);
        let prize = get(userTournament, 'current.olympiad.prize_total', 0);

        // if not user tournament prize, try to get from bignerve prize
        if (!prize) {
            const platformTournament = await useTournament('bignerve');
            prize = get(platformTournament, 'current.olympiad.prize_total', 0);
        }

        target.value.deep_prize = prize && currency(prize);
    } catch (err) {
        console.log(err);

        sentry.withScope((scope) => {
            scope.setTag('event', 'sharing-user');
            scope.setContext('target', target.value);
            scope.setContext('sharing', sharing);

            sentry.captureException(err);
        });
    }
}

function beforeShare(next, provider) {
    const checks = [
        ['twitter'].includes(provider),
        ['challenge'].includes(sharing.subject),
        get(content.value, 'metadata.credentials', []).includes('twitter'),
    ];

    if (checks.every((v) => v)) {
        emit('done');

        busEmit('sharer:share', {
            provider, payload: content.value,
        });

        return;
    }

    next();
}

async function snoozeSharing() {
    try {
        snoozing.value = true;
        const { data: response } = await api.sharing.snooze({
            payload: content.value,
        });

        setSnoozeds(response.data.items);
    } catch (err) {
        console.log(err);

        sentry.withScope((scope) => {
            scope.setTag('event', 'sharing-user');
            scope.setExtra('target', target.value);
            scope.setExtra('sharing', sharing);

            sentry.captureException(err);
        });
    } finally {
        snoozing.value = false;
    }
}

function countSnoozedSharings() {
    if (auth.authenticated) {
        fetchSnoozed().catch(() => {
            sentry.addBreadcrumb({
                category: 'Sharing',
                message: 'Error fetching snoozed sharings',
                level: 'error',
            });
        });
    }
}

function resolveChallengeRoute() {
    if (isAMA(challenge.value)) {
        return {
            name: 'suggestion-box.ama',
            params: { platformSlug: handleAccessor(target.value.catalyst) },
        };
    }

    return {
        name: surveyMode.value && surveyModeAllowed.value ? 'challenges.survey' : 'challenges.show',
        params: { challenge: target.value.slug },
    };
}

function resolveUserRoute() {
    return { name: 'home.user', params: { platformSlug: handleAccessor(target.value.user) } };
}

watch(target, () => prepareSharing(), { deep: true });
watch(surveyMode, () => prepareSharing());

onMounted(() => {
    nextTick(() => {
        prepareSharing();
        countSnoozedSharings();
    });
});
</script>

<template>
    <div class="sharing-popup-panel m-3 transition-xy transition-transform duration-500 ease-in-out">
        <div class="sharing-popup-panel__heade flex items-center justify-between bg-nerve text-white  rounded-t-lg px-4 py-2">
            <div class="flex items-center">
                <NIcon as="share-square-solid" class="mr-2 -mt-1" />
                <h2 class="m-0 leading-none">Share</h2>
            </div>
            <slot name="actions" />
        </div>

        <div class="sharing-popup-panel__body">
            <label class="form-group__label font-family-heading">
                Here is your personalized sharing "scout" URL:
            </label>
            <div class="flex items-center">
                <input ref="input" type="url" readonly class="form-group__input is-gray border" :value="url">
                <button aria-label="button" v-if="canCopy" type="button" class="btn is-orange ml-2 w-25 flex justify-center focus:!outline-none" @click.prevent="copy">
                    {{ copied ? 'Copied!' : 'Copy' }}
                </button>
            </div>

            <div v-if="(!isCaption(challenge) && challenge.catalyst && isOwner && surveyModeAllowed)" class="flex items-center gap-2 py-4 leading-none text-sm px-1 border-bottom-2 border-dust-400 border-dashed">
                <label for="check-survey-mode" class="font-semibold">Enable Survey Mode?</label>
                <input v-model="surveyMode" type="checkbox" name="check-survey-mode" class="h-4 w-4" />
                <span><b>Yes</b> (allows anon replies)</span>
                <NIcon v-tooltip="'Survey mode opens your Question up to a wider group of people; <br/>respondents don\'t need to create an account, and the idea collection <br/>and rating screens are simplified. It\'s best to do this for your <br/>newsletter readers, who are more invested in helping you.'" as="question-circle-solid" class="text-nerve" />
            </div>

            <div class="mt-4">
                <p class="font-family-heading"><b>Post directly to your social channels</b>:</p>

                <NAlert v-if="error" type="danger" open>
                    {{ error }}
                </NAlert>

                <div class="sharing-popup-panel__btns relative">
                    <div v-if="preparing" class="absolute text-gray-700 font-bold inset-0 bg-white bg-opacity-80 flex items-center justify-center">Composing message...</div>

                    <ShareNetwork
                        v-for="network in networks"
                        :network="network.network"
                        :key="network.network"
                        :url="sharing.url"
                        :title="sharing.title"
                        :description="sharing.description"
                        :quote="sharing.quote"
                        :hashtags="sharing.hashtags"
                        :twitterUser="sharing.twitterUser"
                        :disabled="!network.enabled"
                        :before-share="beforeShare"
                        class="sharing-btn"
                        tag="button"
                        @open="emit('done')"
                        @failure="error = $event"
                    >
                        <span class="min-w-8 -mr-1 flex justify-center items-center">
                            <NLoading v-if="network.sharing" class="text-secondary" loading />
                            <NIcon v-else :as="network.icon" />
                        </span>
                        <span>{{ network.name }}</span>
                    </ShareNetwork>

                    <button aria-label="button" type="button" class="sharing-btn is-recruit" disabled>
                        <span aria-hidden="true" class="icon icon-binoculars-solid"></span>
                        Your Recruits
                    </button>
                </div>
            </div>
        </div>
        <div class="sharing-popup-panel__footer border-dust-400">
            <NAlert v-if="snoozed" type="success" open>
                This challenge was saved to share later.
            </NAlert>

            <div class="flex justify-between mb-2">
                <div class="text-center">
                    <div class="flex items-center">
                        <div :class="{ disabled: snoozed || !content || !auth.authenticated }">
                            <button aria-label="button" type="button" :class="{ 'animate animate-pulse': snoozing }" :disabled="snoozing || !content || !auth.authenticated" class="font-bold px-3 py-2 rounded-md bg-nerve text-white hover:bg-nerve-600" @click="snoozeSharing">
                                {{ snoozing ? 'Saving...' : 'Share Later' }}
                            </button>
                        </div>
                        <RouterLink v-if="auth.authenticated && totalSnoozed" :to="{ name: 'home.user.snoozed-sharings', params: { platformSlug: handleAccessor(auth.user, 'me') } }" class="ml-3 !text-nerve hover:bg-nerve-200 font-bold bg-nerve-100 w-8 h-8 flex items-center justify-center rounded-full leading-none !underline-transparent">{{ totalSnoozed }}</RouterLink>
                    </div>
                    <p class="mt-1 mb-0 font-size-0-9-rem"><i>{{ snoozed ? 'Saved' : 'Save' }} to queue</i></p>
                </div>
                <div class="text-center">
                    <div :class="{ disabled: !auth.authenticated }" class="flex items-center">
                        <RouterLink :to="{ name: 'home.user.snoozed-sharings', params: { platformSlug: handleAccessor(auth.user, 'me') } }" type="button" class="font-bold px-3 py-2 rounded-md bg-secondary !text-white hover:bg-secondary-600 hover:!text-white !no-underline">
                            Share Panel
                        </RouterLink>
                        <span aria-hidden="true" class="icon icon-paper-plane-solid is-color-secondary ml-2 font-size-1-4-rem" style="color: #999;"></span>
                    </div>
                    <p class="mt-1 mb-0 font-size-0-9-rem"><i>Check progress</i></p>
                </div>
            </div>
            <p class="mb-0 font-family-heading disabled">
                <b>Automate!</b> Set up your social integrations &amp; automation in the
                <a href="#">Advanced Sharing Panel</a>.
            </p>
        </div>
    </div>
</template>
